import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { generatePath } from 'react-router-dom'
import { mdiArrowLeft } from '@mdi/js'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import ROUTES from 'app/routes'
import { subscriptionDetailsMessages } from './messages'
import {
  SubsDetailsBackIcon,
  SubsDetailsBackLink,
  SubsDetailsHeading,
  PurchaseSubscriptionMainBlock,
  PurchaseSubscriptionSubBlock,
} from './styles'
import { SubscriptionCreditProduct } from '../../../types'
import { useHomeRouteParams } from 'features/home/hooks'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { fetchHomeSubscriptionPriceThunk } from 'features/home/components/HomeSubscription/SubscriptionDetails/slice'
import {
  subscriptionApDevicePriceSelector,
  subscriptionPeriodEndSelector,
  subscriptionsCreditSelector,
} from 'features/home'
import SmartNetworkSubs from './SmartNetworkSubs'
import SubscriptionPlanRenewalDetails from './SubscriptionPlanRenewalDetails'
import PurchaseDeviceTotal from './PurchaseDeviceTotal'
import { fetchHomeThunk } from 'features/home'
import { subscriptionEssentialsPriceSelector } from 'features/home'
import {
  subscribedAccessPointsSelector,
  subscribedEssentialsSelector,
} from 'features/networkAi/selectors'
import { subsribedAccessPointsCountSelector } from 'features/networkAi/selectors'
import {
  selectedHomeSelector,
  activeSubscriptionSelector,
  subscriptionStatusSelector,
} from 'features/home/selectors'
import {
  fetchSubscriptionPricesSliceThunk,
  fetchSubscriptionPeriodEndSliceSliceThunk,
} from 'features/home/components/HomeSubscription/SubscriptionDetails/slice'
import { fetchSubscriptionCreditsThunk } from 'features/home/components/HomeSubscription/slice'

import { isExpiringSoon } from 'features/home/components/HomeSubscription/utils'
import {
  getHomeSubscriptonInfo,
  getHomeNetworkSubscriptonInfo,
} from 'features/home/utils'
import { DateTime } from 'luxon'
import { SpinnerCentered } from 'components/Loader'

const SUBSCRIPTION_STATE = {
  NONE: 'NONE',
  ENROLLED: 'ENROLLED',
  WILL_NOT_RENEW: 'WILL_NOT_RENEW',
  EXPIRES: 'EXPIRES',
}

function SubscriptionRenewals() {
  const { homeId } = useHomeRouteParams()
  const dispatch = useAppDispatch()
  const home = useAppSelector(selectedHomeSelector.data)
  useEffect(() => {
    dispatch(fetchHomeThunk(homeId))
    dispatch(fetchSubscriptionPeriodEndSliceSliceThunk(homeId))
  }, [homeId, dispatch])

  const subscribedAccessPoints = useAppSelector(subscribedAccessPointsSelector)
  const subscribedEssentials = useAppSelector(subscribedEssentialsSelector)
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const subscribedCount = useAppSelector(subsribedAccessPointsCountSelector)
  const apPrice = useAppSelector(subscriptionApDevicePriceSelector)
  const essentialsPrice = useAppSelector(
    subscriptionEssentialsPriceSelector(subscribedEssentials?.hostTier)
  )
  const [totalAmountDue, setTotalAmountDue] = useState(0)
  const activeSubscription = useAppSelector(activeSubscriptionSelector.data)
  //const renewingAP = useAppSelector( networkSubscriptionStatusSelector(subscribedAccessPoints.count) )

  const renewingStatus = useAppSelector(
    subscriptionStatusSelector(1, subscribedAccessPoints.count)
  )
  const renewDate = useAppSelector(subscriptionPeriodEndSelector.data)
  const creditAmounts = useAppSelector(subscriptionsCreditSelector.data)
  const creditAmountsLoading = useAppSelector(
    subscriptionsCreditSelector.isFetching
  )
  const subscriptionInfo = getHomeSubscriptonInfo(home)
  const subscriptionInfoNetwork = getHomeNetworkSubscriptonInfo(home)
  const [essentialsCredits, setEssentialsCredits] = useState(0)
  const [apCredits, setAPCredits] = useState(0)

  useEffect(() => {
    dispatch(fetchSubscriptionPricesSliceThunk(homeId))
  }, [dispatch, homeId])

  const subscriptionState = useMemo(() => {
    if (subscriptionInfo) {
      if (subscriptionInfo.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(subscriptionInfo, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    } else if (subscriptionInfoNetwork) {
      if (subscriptionInfoNetwork.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(subscriptionInfoNetwork, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    }
    return SUBSCRIPTION_STATE.NONE
  }, [subscriptionInfo, subscriptionInfoNetwork])

  useEffect(() => {
    let isMounted = true
    let total = 0
    if (isMounted) {
      if (
        renewingStatus !== undefined &&
        renewingStatus[3] !== 0 &&
        renewingStatus[3] !== undefined
      ) {
        total = renewingStatus[3] * (apPrice?.unit_amount || 0)
      }
      if (
        essentialsPrice !== undefined &&
        subscriptionState === 'ENROLLED' &&
        renewingStatus[1] === 1
      ) {
        setTotalAmountDue(total + essentialsPrice?.unit_amount || 0)
      } else {
        setTotalAmountDue(total)
      }
    }
    return () => {
      isMounted = false
    }
  }, [
    essentialsPrice,
    setTotalAmountDue,
    apPrice,
    renewingStatus,
    subscriptionState,
  ])

  const renewalDate = useMemo(() => {
    if (renewDate !== null) {
      if (
        subscribedAccessPoints.autoRenew === null ||
        subscribedAccessPoints.autoRenew === undefined
      ) {
        return [
          new Date().setFullYear(new Date().getFullYear() + 1),
          renewDate.data,
        ]
      } else {
        return [subscribedAccessPoints.autoRenew, renewDate.data]
      }
    } else {
      if (
        subscribedAccessPoints.autoRenew === null ||
        subscribedAccessPoints.autoRenew === undefined
      ) {
        return [
          new Date().setFullYear(new Date().getFullYear() + 1),
          new Date().setFullYear(new Date().getFullYear() + 1),
        ]
      } else {
        return [
          subscribedAccessPoints.autoRenew,
          subscribedAccessPoints.autoRenew,
        ]
      }
    }
  }, [subscribedAccessPoints, renewDate])

  useEffect(() => {
    dispatch(fetchHomeSubscriptionPriceThunk(homeId))
  }, [dispatch, homeId])

  useEffect(() => {
    const params = { after: renewalDate[1] }
    dispatch(fetchSubscriptionCreditsThunk(homeId, params))
  }, [dispatch, homeId, renewalDate])

  useEffect(() => {
    if (
      creditAmounts !== undefined &&
      creditAmounts?.products.length !== undefined &&
      creditAmounts?.products.length > 0
    ) {
      creditAmounts?.products.forEach(function (
        product: SubscriptionCreditProduct
      ) {
        if (product.productName === 'Savant Essentials') {
          setEssentialsCredits(product.amount)
        } else if (product.productName === 'Smart Network') {
          setAPCredits(product.amount)
        }
      })
    } else {
      setEssentialsCredits(0)
      setAPCredits(0)
    }
  }, [creditAmounts])

  const subscriptionPath = useMemo(
    () =>
      generatePath(ROUTES.homeSubscription.root(), {
        homeId,
        rootDomainId: currentRootId || '',
      }),
    [currentRootId, homeId]
  )

  return (
    <div>
      <SubsDetailsBackLink to={subscriptionPath}>
        <SubsDetailsBackIcon path={mdiArrowLeft} size={1} />
        <FormattedMessage {...subscriptionDetailsMessages.backLink} />
      </SubsDetailsBackLink>
      <SubsDetailsHeading>
        <FormattedMessage {...subscriptionDetailsMessages.title} />
      </SubsDetailsHeading>
      <PurchaseSubscriptionMainBlock>
        <PurchaseSubscriptionSubBlock>
          <SubscriptionPlanRenewalDetails
            isHomeDashboard={false}
            priceId={essentialsPrice?.id || ''}
            paymentMethodId={activeSubscription?.default_payment_method || ''}
            renewingStatus={renewingStatus}
          />
          <SmartNetworkSubs
            apQuantity={subscribedCount}
            renewQuantity={
              renewingStatus !== undefined && renewingStatus[3] !== undefined
                ? renewingStatus[3]
                : 0
            }
            amount={apPrice?.unit_amount ?? 0}
            homeId={homeId}
            renewalDate={renewalDate[1]}
            rootDomainId={currentRootId ?? ''}
            priceId={apPrice?.id || ''}
            paymentMethodId={activeSubscription?.default_payment_method || ''}
            subscriptionState={subscriptionState}
          />
        </PurchaseSubscriptionSubBlock>

        {creditAmountsLoading ? (
          <SpinnerCentered size="40" />
        ) : (
          <PurchaseDeviceTotal
            apQuantity={
              renewingStatus !== undefined && renewingStatus[3] !== undefined
                ? renewingStatus[3]
                : 0
            }
            apPrice={apPrice?.unit_amount ?? 0}
            essentialsPrice={essentialsPrice?.unit_amount || 0}
            totalPrice={totalAmountDue}
            renewalDate={renewalDate[1]}
            subscriptionState={subscriptionState}
            renewingStatus={renewingStatus}
            essentialsCredits={essentialsCredits}
            apCredits={apCredits}
          />
        )}
      </PurchaseSubscriptionMainBlock>
    </div>
  )
}

export default SubscriptionRenewals
